import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  AvatarMenu,
  ErrorLoading,
  IdeaMap,
  Transition,
  useAuthModal,
  useIsMobile,
  WorkshopTopbar,
} from "senf-atomic-design-system";
import { useModals } from "senf-atomic-design-system/src/components/molecules/modalStack/ModalProvider";
import {
  getModeFromSessionStorage,
  useAuthContext,
  useGlobalState,
  usePrepareData,
} from "senf-shared";
import { useHandles } from "../hooks/hooks";
import { setProject } from "../redux/actions/projectActions";
import IdeaDetailPage from "./IdeaDetailPage";
import PostIdeaPage from "./PostIdeaPage";
import ProfilePage from "./ProfilePage";

const TransitionWrapper = ({ children, ...rest }) => {
  const isMobile = useIsMobile();
  return (
    <Transition
      style={{ zIndex: 999 }}
      transitionActive={!isMobile}
      from={{ x: 100, scale: 1.05 }}
      leave={{ x: 100, scale: 1.05 }}
      enter={{ x: 0, scale: 1 }}
      {...rest}
    >
      {children}
    </Transition>
  );
};

const IdeaMapPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {} = useModals();
  const { user } = useAuthContext();
  const [statefulMap, setStatefulMap] = useGlobalState().mapState;
  const isMobile = useIsMobile();
  const profilePageIdeas = useSelector(
    (state) => state.data.profilePage?.profilePageData?.ideas,
  );
  const idea = useSelector((state) => state.data.idea);
  const project = useSelector((state) => state.data.project);
  const { openAuthModal } = useAuthModal(project);
  const { ideaId, projectId, ideaMapId } = useParams();

  const ideaMap = project?.ideaMaps?.find(
    (data) => data.ideaMapId === ideaMapId,
  );

  // Update page title dynamically for better accessibility
  useEffect(() => {
    if (ideaMap?.title) {
      document.title = ideaMap.title;
    } else if (project?.title) {
      document.title = project.title;
    } else {
      // Default fallback
      document.title = "Senf.app";
    }
  }, [ideaMap?.title, project?.title]);

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const rootId = searchParams.get("root");
  const profileId = searchParams.get("profile");

  const errors = useSelector((state) => state.UI.errors);
  const loading = useSelector((state) => state.data.loading);

  useEffect(() => {
    const id = projectId || rootId;
    if (id) {
      dispatch(setProject(id, navigate));
    }
  }, [projectId, rootId]);

  const {
    searchTerm,
    setSearchTerm,
    activeFilters,
    setActiveFilters,
    selectedArealFilter,
    mapBounds,
    setMapBounds,
    setSelectedArealFilter,
    checkedSortOption,
    setCheckedSortOption,
    filteredLikes,
    filteredComments,
    extraColumns,
    filteredData,
  } = usePrepareData("ideas", ideaMap?.ideas, ideaMap, project || {});

  // const dataMap = useMemo(() => {
  //   if (pathname.indexOf("projects") > -1) {
  //     if (selectedTopics.length > 0) {
  //       return ideaMap?.ideas?.filter(({ topic }) =>
  //         selectedTopics.includes(topic)
  //       );
  //     }
  //     return ideaMap?.ideas;
  //   }
  //   if (pathname.indexOf("profile") > -1) {
  //     if (selectedTopics.length > 0) {
  //       return profilePageIdeas?.filter(({ topic }) =>
  //         selectedTopics.includes(topic)
  //       );
  //     }
  //     return profilePageIdeas;
  //   }
  //   return null;
  // }, [profilePageIdeas, profileId, ideaMap?.ideas, selectedTopics, pathname]);

  // const dataFinalMap = useMemo(
  //   () =>
  //     dataMap?.map((object) =>
  //       pick(["title", "lat", "long", "ideaId", "color", "likeCount"], object)
  //     ),
  //   [dataMap]
  // );

  const handle = useHandles(projectId, ideaMapId, ideaId);

  const projectIdeas = ideaMap?.ideas;

  const hide =
    (isMobile && pathname.includes("info")) ||
    (isMobile && pathname.indexOf("addIdea") > -1) ||
    (ideaId && isMobile) ||
    (profileId && isMobile);
  // || pathname.indexOf("info") > -1;

  const [position, setPosition] = useState("bottom");
  const [open, setOpen] = useState(!hide);

  useEffect(() => {
    if (hide) {
      setOpen(false);
      return;
    }
    setOpen(position);
  }, [hide]);

  // const filteredProjectIdeas = useMemo(() => {
  //   const dbKeys = ["title", "body", "localityName", "address"];
  //   return filterDbData(

  //     projectIdeas,
  //     searchTerm,
  //     dbKeys,
  //     selectedTopics,
  //     checkedSortOption,
  //     mapBounds,
  //     selectedArealFilter
  //   );
  // }, [
  //   checkedSortOption,
  //   projectIdeas,
  //   searchTerm,
  //   selectedTopics,
  //   mapBounds,
  //   selectedArealFilter,
  // ]);

  const handleSetMapBounds = () => {
    const bounds = statefulMap.getBounds().toArray();
    const boundsNew = {
      latitude1: bounds[1][1],
      latitude2: bounds[0][1],
      longitude2: bounds[0][0],
      longitude3: bounds[1][0],
    };
    setMapBounds(boundsNew);
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  };
  const handleResetMapBounds = () => {
    setMapBounds(null);
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  };

  const handleSelectArealFilters = (arealFilter) => {
    setSelectedArealFilter(arealFilter);

    if (
      isMobile &&
      (arealFilter === "withAddress" || arealFilter === "inMapArea")
    ) {
      setTimeout(() => {
        setPosition("top");
        setOpen("top");
      }, 0);
    }
    if (arealFilter === "inMapArea") {
      handleSetMapBounds(mapBounds);
    } else {
      handleResetMapBounds();
    }
  };

  return (
    ideaMap && (
      <>
        {getModeFromSessionStorage() === "workshop" &&
          user?.userId &&
          !isMobile && <WorkshopTopbar />}

        <TransitionWrapper
          show={
            pathname.indexOf("addIdea") > -1 ||
            pathname.indexOf("editIdea") > -1
          }
        >
          <PostIdeaPage
            ideaMap={ideaMap}
            project={project}
            handle={handle}
            statefulMap={statefulMap}
          />
        </TransitionWrapper>

        <TransitionWrapper show={searchParams?.get("profile")}>
          <ProfilePage handle={handle} />
        </TransitionWrapper>

        <TransitionWrapper
          show={
            !!(ideaId && pathname.indexOf("editIdea") === -1) &&
            !searchParams?.get("profile")
          }
        >
          <IdeaDetailPage ideaMap={ideaMap} handle={handle} user={user} />
        </TransitionWrapper>

        <IdeaMap
          ideaMap={ideaMap}
          handle={handle}
          user={user}
          pathname={pathname}
          idea={idea}
          ideaId={ideaId}
          profileId={profileId}
          searchParams={searchParams}
          statefulMap={statefulMap}
          setStatefulMap={setStatefulMap}
          project={project}
          dataFinalMap={filteredData}
          projectIdeas={projectIdeas}
          filteredProjectIdeas={filteredData}
          selectedArealFilter={selectedArealFilter}
          setSelectedArealFilter={handleSelectArealFilters}
          open={open}
          setOpen={setOpen}
          setPosition={setPosition}
          checkedSortOption={checkedSortOption}
          setCheckedSortOption={setCheckedSortOption}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          activeFilters={activeFilters}
          setActiveFilters={setActiveFilters}
          accessibilityInfotext={
            pathname.indexOf("addIdea") === -1 &&
            pathname.indexOf("editIdea") === -1
              ? t("map:accessibilityInfotext.movingInfo")
              : t("map:accessibilityInfotext.drawingInfo.pin")
          }
        />

        {errors && !loading && <ErrorLoading />}
        {pathname.indexOf("info") === -1 &&
          pathname.indexOf("addIdea") === -1 &&
          pathname.indexOf("editIdea") === -1 && (
            <nav className={"fixed top-5 z-50 end-5"}>
              <AvatarMenu
                navigate={navigate}
                handle={handle}
                user={user}
                variant="ideaMap"
                project={project}
              />
            </nav>
          )}
      </>
    )
  );
};

export default IdeaMapPage;
